import { useState } from 'react';
import { useData } from './DataContext.jsx';
import { capitalizeName } from './lib/format.js';
import { Modal, Avatar } from './components.jsx';
import { MentionTextarea } from './mention-input.jsx';
import { IconX } from './icons.jsx';

export const FEEDBACK_CATEGORIES = [
  { key: 'bottleneck', label: 'Engpass',            emoji: '🚧', color: 'var(--crit)' },
  { key: 'overtime',   label: 'Zeitüberschreitung', emoji: '⏱️', color: 'var(--warn)' },
  { key: 'praise',     label: 'Lob',                emoji: '🎉', color: 'var(--ok)'   },
  { key: 'general',    label: 'Allgemein',          emoji: '💬', color: 'var(--ink-2)'},
];

const PLACEHOLDERS = {
  bottleneck: 'Wo lief es zäh? Was hat aufgehalten, und wie ließe es sich nächstes Mal lösen?',
  overtime: 'Welche Aufgabe hat deutlich länger gedauert als geplant? Woran lag es?',
  praise: 'Was lief richtig gut? Mach es konkret — das motiviert.',
  general: 'Deine Rückmeldung ans Team …',
};

// Admin composer to reflect improvement potential back to team members.
//
// Props:
//   presetRecipientIds – ids to preselect (e.g. everyone involved in a project)
//   project            – optional project the feedback refers to
//   context            – optional prefilled metric snapshot ("142% Budget · 8 Tage über Deadline")
//   defaultCategory    – one of FEEDBACK_CATEGORIES keys
//   onClose, onSent
function FeedbackModal({ presetRecipientIds = [], project = null, context = '', defaultCategory = 'general', onClose, onSent }) {
  const { profiles, currentProfile, addFeedback, syncMentions } = useData();

  // Selectable recipients: everyone except the admin themselves.
  const candidates = profiles.filter(p => p.id !== currentProfile?.id);

  const [selected, setSelected] = useState(() => new Set(presetRecipientIds.filter(id => id !== currentProfile?.id)));
  const [category, setCategory] = useState(defaultCategory);
  const [message, setMessage] = useState('');
  const [ctx, setCtx] = useState(context || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const cat = FEEDBACK_CATEGORIES.find(c => c.key === category) || FEEDBACK_CATEGORIES[3];
  const canSend = selected.size > 0 && message.trim().length > 0 && !busy;

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleSend() {
    if (!canSend) return;
    setBusy(true); setErr(null);
    try {
      const rows = [...selected].map(to_user_id => ({
        to_user_id,
        project_id: project?.id || null,
        category,
        message: message.trim(),
        context: ctx.trim() || null,
      }));
      await addFeedback(rows);
      // Notify anyone @-mentioned inside the feedback message.
      await syncMentions({
        text: message,
        sourceType: 'feedback',
        projectId: project?.id || null,
      });
      onSent?.(rows.length);
      onClose();
    } catch (e) {
      setErr(e.message || 'Fehler beim Senden');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={busy ? () => {} : onClose}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div className="eyebrow">Feedback ans Team</div>
          <h2 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: '4px 0 0', letterSpacing: '-0.01em' }}>
            {project ? project.title : 'Rückmeldung geben'}
          </h2>
          {context && (
            <div className="tiny muted mono" style={{ marginTop: 6 }}>{context}</div>
          )}
        </div>
        <button className="icon-btn" onClick={onClose} disabled={busy}><IconX size={14}/></button>
      </div>

      <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
        {/* Category */}
        <div className="eyebrow" style={{ marginBottom: 8 }}>Art</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          {FEEDBACK_CATEGORIES.map(c => {
            const active = c.key === category;
            return (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className="btn btn-ghost"
                style={{
                  borderColor: active ? c.color : 'var(--stroke)',
                  color: active ? c.color : 'var(--ink-2)',
                  background: active ? 'var(--bg-hover)' : 'transparent',
                  fontWeight: active ? 600 : 500,
                }}
              >
                <span style={{ marginRight: 6 }}>{c.emoji}</span>{c.label}
              </button>
            );
          })}
        </div>

        {/* Recipients */}
        <div className="eyebrow" style={{ marginBottom: 8 }}>Empfänger ({selected.size})</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
          {candidates.length === 0 && (
            <div className="tiny muted">Keine weiteren Teammitglieder vorhanden.</div>
          )}
          {candidates.map(u => {
            const active = selected.has(u.id);
            return (
              <button
                key={u.id}
                onClick={() => toggle(u.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 10px 5px 5px', borderRadius: 999, cursor: 'pointer',
                  background: active ? 'var(--accent-soft)' : 'var(--bg-sunken)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--stroke)'}`,
                  color: active ? 'var(--ink)' : 'var(--ink-2)',
                  fontSize: 12.5, fontWeight: active ? 600 : 500,
                }}
              >
                <Avatar user={u} size={22}/>
                {capitalizeName(u.full_name) || u.email}
              </button>
            );
          })}
        </div>

        {/* Context (editable snapshot) */}
        <div className="eyebrow" style={{ marginBottom: 8 }}>Bezug <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></div>
        <input
          className="input"
          value={ctx}
          onChange={e => setCtx(e.target.value)}
          placeholder="z. B. Projekt X · 142% Budget · 8 Tage über Deadline"
          style={{ width: '100%', marginBottom: 18 }}
        />

        {/* Message */}
        <div className="eyebrow" style={{ marginBottom: 8 }}>Nachricht</div>
        <MentionTextarea
          value={message}
          onChange={setMessage}
          profiles={profiles}
          placeholder={PLACEHOLDERS[category]}
          style={{ minHeight: 120, width: '100%' }}
        />

        {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 12 }}>{err}</div>}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--stroke)', padding: '14px 24px' }}>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Abbrechen</button>
        <button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={!canSend}
          style={{ opacity: canSend ? 1 : 0.45 }}
        >
          {busy ? 'Sende…' : `Senden${selected.size > 1 ? ` (${selected.size})` : ''}`}
        </button>
      </div>
    </Modal>
  );
}

export { FeedbackModal };
